<template>
  <div class="saas-services-page">
    <!-- Hero section -->
    <!-- Header with gradient -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Our SaaS solutions</h1>
        <p class="subtitle">Grow your business with our AI-enhanced tools</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">Transform your business with our SaaS solutions</h2>
          <div class="text-and-illustration">
            <div class="text-content">
              <p>
                Discover our innovative SaaS solutions, designed to optimize and facilitate the management of your business through artificial intelligence. Our tools are custom-developed to meet your specific professional needs.
              </p><br>
              <p>
                ✓ <strong><i>Customizable features</i></strong> : The SaaS integrates seamlessly into your company's existing workflows and fulfills your needs.
              </p><br>
              <p>
                ✓ <strong><i>Security and compliance</i></strong> : We conceive the technical infrasctructure to ensure that your data is protected according to reglementation and that it is robust enough to allow high availability as well as able to scale with your company's growth.
              </p><br>
              <p>
                ✓ <strong><i>Dedicated support and guidance</i></strong> : Beyond the product itself, our personalized support includes a structured implementation phase, customized training, comprehensive documentation, and responsive technical support with dedicated points of contact.
              </p>              
            </div>
            <div class="illustration-container">
              <iframe 
                style="width: 100%; height: 100%; border: none; border-radius: 10px;"
                src="https://www.youtube.com/embed/NGf2YgDIgiI" 
                title="Presentation of our SaaS solutions"
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen>
              </iframe>
            </div>
          </div>
        </div>
      </section>

    <!-- Presentation of SaaS solutions -->
    <section class="solutions-section">
      <div class="container">
        <h2 class="section-title">Discover our SaaS solutions</h2>
        <p class="section-subtitle">Custom tools enhanced by AI to meet your professional needs</p>
        
        <!-- Pulsar IA -->
        <div class="solution-card">
          <div class="solution-content">
            <h3 class="solution-title data-title">Pulsar IA</h3>
            <div class="solution-tagline">The first fully AI-automated customer engagement platform</div>
            <div class="solution-description">
              <p>
                <strong>Pulsar IA</strong> is more than just a CRM: it's a complete AI infrastructure that provides you with a team of AI agents expert in digital marketing, business relations and customer service, working for you 24/7 and 7/7.
              </p>
              <p>
                Our solution transforms the way you interact with your prospects and customers. With Pulsar IA, you save up to 20 hours of work per week by automating repetitive tasks: prospecting, lead collection, appointment management, community management, Google review collection, customer support and loyalty.
              </p>
              <p>
                Pulsar IA adapts to all types of businesses (real estate agencies, consulting firms, e-commerce, artisans) and natively integrates all necessary tools: AI chatbot, call center, newsletters, marketing campaigns, appointment scheduling and unified contact database.
              </p>
            </div>
            <div class="solution-features">
              <h4>Integrated AI agents to automate your business</h4>
              <ul>
                <li>Expert AI chatbot connected to your knowledge base</li>
                <li>AI call center that automatically answers 95% of calls</li>
                <li>Fully automated email and SMS marketing campaigns</li>
                <li>Personalized newsletters generated and sent by AI</li>
                <li>AI community manager for your social networks</li>
                <li>Automatic appointment scheduling with calendar synchronization</li>
                <li>Unified and segmented contact database</li>
              </ul>
            </div>
            <div class="solution-testimonials">
              <div class="testimonial">
                <div class="testimonial-content">
                  "IA Agency has revolutionized the way we work. We increased the frequency of our posts and our presence on social networks by 200%."
                </div>
                <div class="testimonial-author">Marie D, Marketing Director</div>
              </div>
            </div>
            <div class="solution-cta">
              <ButtonCTA to="https://iaagence.fr/">Learn more</ButtonCTA>
            </div>
          </div>
        </div>
        
        <!-- Meeting Transcript -->
        <div class="solution-card">
          <div class="solution-content">
            <h3 class="solution-title meeting-title">Meeting Transcript</h3>
            <div class="solution-tagline">AI-assisted meeting transcription and analysis</div>
            <div class="solution-description">
              <p>
                <strong>Meeting Transcript</strong> is an all-in-one solution that optimizes and facilitates meeting management through artificial intelligence. Our tool automatically converts your meeting audio into text with exceptional accuracy, while recognizing different speakers.
              </p>
              <p>
                Beyond simple transcription, Meeting Transcript automatically generates clear summaries and relevant syntheses after each meeting, allowing your team to save considerable time and improve productivity.
              </p>
              <p>
                Our solution adapts to many professional contexts: teamwork, recruitment, education, healthcare, commerce, and journalism. The platform is simple to use and easily integrates with your existing tools, with end-to-end encryption compliant with GDPR.
              </p>
            </div>
            <div class="solution-features">
              <h4>Features optimized by a team of AI agents</h4>
              <ul>
                <li>All-in-one platform: audio conferencing and transcription</li>
                <li>Audio recording of conferences</li>
                <li>AI audio-to-text transcription with speaker recognition</li>
                <li>Automated generation of downloadable summaries</li>
                <li>Import of external audio files for transcription with accuracy > 98%</li>
                <li>Processing in less than an hour for standard meetings</li>
                <li>Support for common audio formats (MP3, OGG, WAV, FLAC)</li>
              </ul>
            </div>
            <div class="solution-testimonials">
              <div class="testimonial">
                <div class="testimonial-content">
                  "Meeting Transcript has revolutionized the way we collaborate. We've reduced the time spent taking notes by 70%."
                </div>
                <div class="testimonial-author">Marie Dupont, Marketing Director</div>
              </div>
            </div>
            <div class="solution-cta">
              <ButtonCTA to="https://www.meetingtranscript.fr/">Learn more</ButtonCTA>
            </div>
          </div>
        </div>
        
        <!-- SEO Boost -->
        <div class="solution-card">
          <div class="solution-content">
            <h3 class="solution-title seo-title">SEO Boost</h3>
            <div class="solution-tagline">Optimize your web ranking with AI technologies</div>
            <div class="solution-description">
              <p>
                <strong>SEO Boost</strong> is a comprehensive tool that helps you boost your visibility on search engines. Our solution performs a detailed SEO audit of your web pages to identify technical issues, indexing problems, and optimization opportunities.
              </p>
              <p>
                Our AI tool compares your web page with competitors and identifies possible optimizations on SEO metrics that really matter. You get a complete analysis including the top 10 competitors on Google, a comparison on key SEO metrics, and simple, easy-to-read graphs.
              </p>
              <p>
                SEO Boost also performs an in-depth semantic analysis, comparing the semantic field of your web page with competitors to help you discover strategic keywords to use in your content. Data is presented in real-time and the interface is user-friendly and intuitive.
              </p>
            </div>
            <div class="solution-features">
              <h4>Features to strengthen your online presence</h4>
              <ul>
                <li>In-depth analysis of your web page's SEO</li>
                <li>Identification of indexing issues and status analysis</li>
                <li>Comparison with the top 10 competitors on Google</li>
                <li>Semantic analysis and keyword wordcloud</li>
                <li>Simple visualization of essential metrics</li>
                <li>Optimization suggestions for your content</li>
                <li>Exportable reports and data in Excel format</li>
              </ul>
              </div>
              <div class="solution-testimonials">
              <div class="testimonial">
                  <div class="testimonial-content">
                  "SEO Boost is super easy to use. I was able to see who my real competitors were and create new content better suited to my readers' expectations."
                  </div>
                  <div class="testimonial-author">Jennifer, Fashion Influencer and Blogger</div>
              </div>
              </div>
              <div class="solution-cta">
              <ButtonCTA to="https://www.graine-digitale.fr/boostez_votre_seo.html">Learn more</ButtonCTA>
              </div>
          </div>
          </div>
          
          <!-- Contact Finder Pro -->
          <div class="solution-card">
          <div class="solution-content">
              <h3 class="solution-title contact-title">Contact Finder Pro</h3>
              <div class="solution-tagline">Intelligent prospecting and contact management solution</div>
              <div class="solution-description">
              <p>
                <strong>Contact Finder Pro</strong> is our advanced solution for commercial prospecting and professional contact management. This tool allows you to quickly identify and qualify relevant prospects for your business using artificial intelligence.
              </p>
              <p>
                Our solution provides you with accurate and up-to-date contacts, with different ways to contact companies, their email coordinates, phone, and social networks. Contact Finder Pro thus facilitates data enrichment to complete your existing databases.
              </p>
              <p>
                The tool allows you to segment your prospects according to their online presence or on social networks and automate your prospecting campaigns. It integrates perfectly with your existing CRM tools and scrupulously respects GDPR standards for compliant use.
              </p>
              </div>
              <div class="solution-features">
              <h4>Features designed to optimize contact search</h4>
              <ul>
                  <li>Identification of qualified prospects in real-time</li>
                  <li>Automatic enrichment of your databases</li>
                  <li>Segmentation for targeted campaigns</li>
                  <li>Native integration with your existing CRM tools</li>
                  <li>Data export in standard formats</li>
                  <li>Guaranteed GDPR compliance</li>
              </ul>
              </div>
              <div class="solution-testimonials">
              <div class="testimonial">
                  <div class="testimonial-content">
                  "Thanks to Contact Finder Pro, we increased our conversion rate by 45% by precisely targeting the right decision-makers in our sector."
                  </div>
                  <div class="testimonial-author">Thomas Martin, Sales Director</div>
              </div>
              </div>
              <div class="solution-cta">
              <ButtonCTA to="https://www.graine-digitale.fr/contact_finder_facile.html">Learn more</ButtonCTA>
              </div>
          </div>
          </div>
      </div>
      </section>
      
      
      <!-- FAQ Section -->
      <section class="faq-section">
      <div class="container">
          <h2 class="section-title">Frequently asked questions</h2>

          <div class="faq-list">
          <div class="faq-item">
              <div class="faq-question">
              I need to develop a SaaS, how does it work?
              </div>
              <div class="faq-answer">
              It's very simple! Together we write the specifications and decide the roadmap. Next, we develop the different features. In the meantime, your teams test the product and we make adjustments based on your feedback. Finally, we deploy the SaaS to production. Afterward, you benefit from a few additional weeks to fine-tune the final details.
              </div>
          </div>          
          
          <div class="faq-item">
              <div class="faq-question">
              Are your SaaS solutions GDPR compliant ?
              </div>
              <div class="faq-answer">
              Yes, all our solutions are fully compliant with the General Data Protection Regulation (GDPR). We use end-to-end encryption to secure your data and we do not store any information not necessary for the operation of our services.
              </div>
          </div>
          
          <div class="faq-item">
              <div class="faq-question">
              How does the "lifetime" offer work ?
              </div>
              <div class="faq-answer">
              Our lifetime offer gives you permanent access to our solutions without any additional monthly or annual payment. It is valid for the entire duration of existence of our services, with a minimum guarantee of 3 years. You also benefit from all future updates of the concerned solutions.
              </div>
          </div>
          
          <div class="faq-item">
              <div class="faq-question">
              Can I try your solutions before committing ?
              </div>
              <div class="faq-answer">
              Absolutely! For SEO Boost and Contact Finder Pro, we offer a free tier allowing you to test our solutions with a limited number of monthly requests. This plan allows you to evaluate our tools before upgrading to a paid plan. No credit card is required for registration.
              </div>
          </div>
          
          <div class="faq-item">
              <div class="faq-question">
              What support is included with your solutions ?
              </div>
              <div class="faq-answer">
              Paid plans include technical support by email with a guaranteed response within 24 hours. Additionally, the Bundle offer benefits like priority support. If you wish, we can even organize a personalized training session to help you get the most out of our solutions.
              </div>
          </div>
          </div>
      </div>
      </section>
      
      <!-- Final call to action -->
      <section class="cta-section">
      <div class="container">
          <h2 class="cta-title">Ready to transform your business ?</h2>
          <p class="cta-subtitle">Join thousands of companies using our SaaS solutions</p>
          <div class="cta-buttons">
            <span class="btn-secondary fake-link" @click="navigateTo('e1')">Schedule an online / phone / video appointment</span>
          </div>
      </div>
      </section>
    </div>
    </div>
  </template>

  <script>
  import ButtonCTA from '@/components/ui/ButtonCTA.vue'
  import { useHead } from '@vueuse/head'

  export default {
    name: 'SaasServicesPage',
    components: {
      ButtonCTA
    },
    setup() {
      useHead({
        title: 'SaaS creation - Web application development | Arynae',
        meta: [
          { name: 'description', content: 'Custom development of SaaS (Software as a Service) solutions. Create your web application with our technical expertise and user-centered design.' },
          { name: 'keywords', content: 'SaaS creation, software development, web application, software as a service, custom development, MVP, user interface, UX, UI, Arynae' },
          { name: 'robots', content: 'index, follow' },
          { name: 'author', content: 'Arynae' },
          { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
          // Essential Open Graph
          { property: 'og:type', content: 'website' },
          { property: 'og:title', content: 'SaaS creation - Web application development | Arynae' },
          { property: 'og:description', content: 'Custom development of SaaS (Software as a Service) solutions. Create your web application with our technical expertise and user-centered design.' },
          { property: 'og:url', content: 'https://arynae.com/en/saas' },
          { property: 'og:locale', content: 'en_US' },
          { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
          { property: 'og:image:alt', content:'Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
          // Basic Twitter Card
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: 'SaaS creation - Web application development | Arynae' },
          { name: 'twitter:description', content: 'Custom development of SaaS (Software as a Service) solutions. Create your web application with our technical expertise and user-centered design.' },
          { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
        ],
        link: [
          { rel: 'canonical', href: 'https://arynae.com/en/saas' },
          { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/saas' },
          { rel: 'alternate', hreflang: 'en', href: 'https://arynae.com/en/saas' }
        ],
        htmlAttrs: {
          lang: 'en'
        }
      })
      
      return {}
    },
    data() {
    return {}
  },
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    }
  }
}
  </script>

  <style lang="scss" scoped>
@import '@/assets/styles/_variables.scss';


.saas-services-page {
  font-family: Arial, sans-serif;
  color: #333;
}

/* Header Styles */
.header {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  padding-bottom: 6rem;
  color: white;
}

.header-content {
  text-align: center;
  padding: 5rem 2rem 3rem;
}

.main-title {
  font-size: 2.75rem;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
}

/* Content Container */
.content-container {
  max-width: 1200px;
  margin: -3rem auto 4rem;
  padding: 0 2rem;
}

/* Card Styles */
.card {
  background: linear-gradient(to bottom, #ffffff, #f3f4f6);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-content {
  padding: 2rem;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  display: block;
}

.text-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

/* Intro Card with Illustration */
.text-and-illustration {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.text-and-illustration .text-content {
  flex: 1;
  min-width: 300px;
}

/* Juste après ces règles CSS existantes */
.illustration-container {
  flex: 0.7;
  min-width: 200px;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Ajoutez uniquement cette media query */
@media (max-width: 768px) {
  .text-and-illustration {
    flex-direction: column;
  }
  
  .text-and-illustration .text-content,
  .illustration-container {
    min-width: 0;
    width: 100%;
  }
}

  // Section Solutions
  .solutions-section {
  padding: $spacing-xl 0;
  
  .section-title {
      font-size: 36px;
      font-weight: bold;
      color: $dark-text;
      margin-bottom: $spacing-sm;
      text-align: center;
  }
  
  .section-subtitle {
      font-size: 18px;
      color: $medium-text;
      margin-bottom: $spacing-xl;
      text-align: center;
  }
  
  .solution-card {
      background-color: $white;
      border-radius: $border-radius-xl;
      padding: $spacing-xl;
      margin-bottom: $spacing-xl;
      box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
      
      .solution-content {
      width: 100%;
      }
  }
  
  .solution-title {
      font-size: 30px;
      font-weight: bold;
      margin-bottom: $spacing-xs;
      
      &.meeting-title {
      color: #7400b8;
      }
      
      &.seo-title {
      color: #5390d9;
      }
      
      &.contact-title {
      color: #ff006e;
      }
      
      &.data-title {
      color: rgb(59, 130, 246);
      }
  }
  
  .solution-tagline {
      font-size: 18px;
      color: $medium-text;
      margin-bottom: $spacing-md;
  }
  
  .solution-description {
      margin-bottom: $spacing-md;
      
      p {
      margin-bottom: $spacing-sm;
      line-height: 1.6;
      }
  }
  
  .solution-features {
      margin-bottom: $spacing-md;
      
      h4 {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: $spacing-sm;
      }
      
      ul {
      list-style-type: disc;
      padding-left: 20px;
      
      li {
          margin-bottom: $spacing-xs;
      }
      }
  }
  
  .solution-testimonials {
      background-color: $light-gray;
      padding: $spacing-md;
      border-radius: $border-radius-md;
      margin-bottom: $spacing-md;
      
      .testimonial-content {
      font-style: italic;
      margin-bottom: $spacing-xs;
      }
      
      .testimonial-author {
      font-weight: bold;
      }
  }
  
  .solution-cta {
      margin-top: $spacing-md;
  }
  }

  // Section FAQ
  .faq-section {
  padding: $spacing-xl 0;
  
  .section-title {
      font-size: 36px;
      font-weight: bold;
      color: $dark-text;
      margin-bottom: $spacing-xl;
      text-align: center;
  }
  
  .faq-list {
      max-width: 800px;
      margin: 0 auto;
  }
  
  .faq-item {
      margin-bottom: $spacing-md;
      border-bottom: 1px solid $light-gray;
      padding-bottom: $spacing-md;
      
      &:last-child {
      border-bottom: none;
      }
  }
  
  .faq-question {
      font-size: 18px;
      font-weight: bold;
      color: $dark-text;
      margin-bottom: $spacing-sm;
      cursor: pointer;
  }
  
  .faq-answer {
      color: $medium-text;
      line-height: 1.6;
  }
  }

  // Section CTA
  .cta-section {
  padding: $spacing-xl 0;
  background: $accent-gradient;
  color: $white;
  text-align: center;
  
  .cta-title {
      font-size: 36px;
      font-weight: bold;
      margin-bottom: $spacing-sm;
  }
  
  .cta-subtitle {
      font-size: 18px;
      margin-bottom: $spacing-lg;
      opacity: 0.9;
  }
  
  .cta-buttons {
      display: flex;
      justify-content: center;
      gap: $spacing-md;
      
      @media (max-width: $breakpoint-sm) {
      flex-direction: column;
      align-items: center;
      }
  }
  }

  // Styles des boutons
  .btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: bold;
  color: $white;
  background-color: transparent;
  border: 2px solid $white;
  border-radius: $border-radius-pill;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      transform: translateY(-2px);
  }
  
  .cta-section & {
      border-color: $white;
      color: $white;
  }
  
  .hero-section & {
      border-color: #5e60ce;
      color: #5e60ce;
  }
  }

  /* Ajoutez après les règles de .solutions-section */
@media (max-width: 768px) {
  .solutions-section .container {
    padding: 0;
  }
  
  .solution-card {
    padding: 1.5rem;
  }
}



  </style>